import React from 'react';
import PropTypes from 'prop-types';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { golf, breakpoints } from './enums';
import eidBuilder from './eid';
import { TrayMenu, TrayTitle } from '@ux/tray-menu';
import Link from '@ux/link';
import Icon from '@ux/icon';

/**
 * Render and expose the LanguageSelector as a functional Component.
 *
 * @param {Object} props Properties
 * @api public
 */
class ContactTray extends NamespaceConsumer {
  constructor(props) {
    super(...arguments);

    this.eids = {
      contactUs: eidBuilder({ key: golf.contactUs, manifest: props.manifest }),
      globalDirectory: eidBuilder({ key: golf.globalDirectory, manifest: props.manifest }),
      help: eidBuilder({ key: golf.help, manifest: props.manifest })
    };

    this.showSupportContacts = this.showSupportContacts.bind(this);
    this.state.showPhoneNumbers = false;
  }

  /**
   * Add eventListeners for breakpoints.
   *
   * @api private
   */
  componentDidMount() {
    this.breakpoint(...breakpoints);
  }

  /**
   * Remove the eventListeners of breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  render() {
    const props = this.props;

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        { this.renderInternal() }
      </IntlProvider>
    );
  }

  /**
   * Render contact tray menu or link
   * @returns {React.Element} tray menu or link
   */
  renderInternal() {
    const props = this.props;
    const supportDetails = props.supportContacts || { };
    const phoneNumber = supportDetails.technicalSupportPhone || '';
    const trayName = supportDetails.technicalSupportLabel;
    const telHref = `tel:${phoneNumber.replace(/[^\d]/g, '')}`;

    if (!trayName || trayName === '') {
      return <div/>;
    }

    const includesPhoneNumber = this.includesPhoneNumber(trayName, phoneNumber);

    if (props.privateLabelId === 1) {
      if (props.contactTrayEnabled) {
        return this.renderFullTray(supportDetails, trayName, includesPhoneNumber);
      }

      if (this.shouldShowCityContactsTray(supportDetails)) {
        return this.renderCityContactsTray(supportDetails, trayName, telHref, includesPhoneNumber);
      }
    }

    // If none of the trays are enabled, just show the tray name as a link
    // If {phone} make the number a telHref otherwise link to contact us
    const trayNameEid = includesPhoneNumber ?
      `uxp.hyd.utility_bar.support_phone.click`
      : this.eids.contactUs;

    let trayHref = props.urls.contactUs.href;
    if (includesPhoneNumber) {
      trayHref = telHref;
    } else if (props.privateLabelId !== 1) {
      trayHref = '#';
    }

    return (
      <div className={ this.namespace('basic-phone-container') }>
        <Link urlArgs={ props.urlArgs }
          href={ trayHref }
          className={ this.namespace('btn', 'basic-phone-btn') }
          data-eid={ trayNameEid }>
          { this.renderTrayName(trayName, includesPhoneNumber) }
        </Link>
      </div>
    );
  }

  /**
   * Renders name to display on contact tray
   * @param  {String} trayName The translated text to display as the tray name
   * @param {Boolean} includesPhoneNumber whether or not the tray label includes the support number
   * @returns {React.Element} div containing tray name
   * @private
   */
  renderTrayName(trayName, includesPhoneNumber) {
    const props = this.props;
    const isIconsContactHelpExp = props.split === 'iconsContactHelpExp';

    return (
      <React.Fragment>
        { (this.isMobile || this.state.breakpoint === 'tablet' || includesPhoneNumber || isIconsContactHelpExp) && this.renderPhoneIcon() }
        <span className={ this.namespace('basic-phone-text', 'hidden-sm-down', 'title-text') }>
          { trayName }
        </span>
      </React.Fragment>
    );
  }

  /**
   * Check if tray name includes the phone number
   * @param {String} trayName contact tray label
   * @param {String} phoneNumber the technical support number
   * @returns {Boolean} true if tray name includes phone number
   * @private
   */
  includesPhoneNumber(trayName, phoneNumber) {
    return trayName.indexOf(phoneNumber) !== -1;
  }

  /**
   * Whether to show the city contacts tray
   *
   * Only shows the tray if there exists a city contact with a phone number that
   * does not match the market contact
   *
   * @param {Object} supportDetails Information from the support API
   * @returns {Boolean} true if we should show the city contacts tray, false otherwise
   * @private
   */
  shouldShowCityContactsTray(supportDetails) {
    if (!supportDetails || !supportDetails.cityContacts)
      return false;

    const cityContactKeys = Object.keys(supportDetails.cityContacts);
    for (let i = 0; i < cityContactKeys.length; i++) {
      const contact = supportDetails.cityContacts[cityContactKeys[i]];
      if (contact.technicalSupportPhone !== supportDetails.technicalSupportPhone) {
        return true;
      }
    }

    return false;
  }

  /**
   * Render the phone icon along with appropriate alternate text
   *
   * @returns {ReactElement} A `span` element with the phone icon and specified alt text
   * @private
   */
  renderPhoneIcon() {
    const props = this.props;

    return (
      <div title={ props.messages[golf.contactUs] } className={ this.namespace('uxicon', 'uxicon-phone-header') }></div>
    );
  }

  renderCityContacts(supportDetails) {
    const altContactsData = [];

    // Push alternative contacts
    if (supportDetails.cityContacts && Object.keys(supportDetails.cityContacts).length) {
      Object.keys(supportDetails.cityContacts).forEach(key => {
        altContactsData.push(supportDetails.cityContacts[key]);
      });
    }

    // If no city contacts were found, use market contact
    // Make sure to use technicalSupportDescription for the market contact
    if (!altContactsData.length) {
      altContactsData.push({
        technicalSupportPhone: supportDetails.technicalSupportPhone,
        technicalSupportHours: supportDetails.technicalSupportDescription
      });
    }

    const altContacts = altContactsData.map((contact, idx) => {
      const altPhone = contact.technicalSupportPhone || '';
      const hrefPhoneNumber = `tel:${altPhone.replace(/[^\d]/g, '')}`;
      // If {phone} make the number a telHref otherwise link to contact us
      const trayNameEid = this.includesPhoneNumber ?
        `uxp.hyd.utility_bar.support_phone.click`
        : this.eids.contactUs;

      return (
        <li key={ idx }>
          <span>
            { contact.technicalSupportHours }
          </span>
          <a className={ this.namespace('contact-link') }
            data-eid={ trayNameEid  }
            href={ hrefPhoneNumber }>
            { altPhone }
          </a>
        </li>
      );
    });

    return (
      <ul className={ this.namespace('alt-contact-list', 'list-unstyled') }>
        { altContacts }
      </ul>
    );
  }

  renderFederatedPartner() {
    const props = this.props;

    if (!props.federatedPartner) {
      return null;
    }

    const { phone, phoneDescription } = props.federatedPartner;
    const hrefPhoneNumber = `tel:${phone.replace(/[^\d]/g, '')}`;

    return (
      <div className={ this.namespace('federated-partner') }>
        <div className={ this.namespace('partner-description') }>{ phoneDescription }</div>
        <a
          href={ hrefPhoneNumber }
          className={ this.namespace('contact-link') }
          data-eid='uxp.hyd.utility_bar.federated_phone.click'>
          { phone }
        </a>
      </div>
    );
  }

  renderCityContactsTray(supportDetails, trayName, includesPhoneNumber) {
    const trayLabel = this.renderTrayName(trayName, includesPhoneNumber);
    const { messages = {} } = this.props;

    return (
      <TrayMenu
        ariaLabel={ messages[golf.contactUs] }
        data-eid='uxp.hyd.utility_bar.city_contact_tray.tray.click'
        type='base'
        className={ this.namespace('city-contacts-tray') }
        name={ trayLabel }>
        <div className={ this.namespace('container') }>
          <div className={ this.namespace('row') }>
            <TrayTitle>
              <FormattedMessage id='Shared:UtilityBar:SupportPhone:Alternate:Numbers' />
            </TrayTitle>
            { this.renderCityContacts(supportDetails) }
            { this.renderFederatedPartner() }
            { this.renderSupportAnnouncement(supportDetails.technicalSupportAnnouncement) }
          </div>
        </div>
      </TrayMenu>
    );
  }

  renderFullTray(supportDetails, trayName, includesPhoneNumber) {
    const props = this.props;
    const eids = this.eids;
    const trayLabel = this.renderTrayName(trayName, includesPhoneNumber);
    const isIconsContactHelpExp = props.split === 'iconsContactHelpExp';

    return (
      <TrayMenu
        ariaLabel={ props.messages[golf.contactUs] }
        data-eid='uxp.hyd.utility_bar.contact_tray.tray.click'
        type='base'
        className={ this.namespace(this.classNames('contact-tray', {
          'contact-icon-exp': isIconsContactHelpExp
        })) }
        name={ trayLabel }>
        <div className={ this.namespace('container') }>
          { this.renderSupportAnnouncement(supportDetails.technicalSupportAnnouncementAllChannels, true, {
            label: supportDetails.technicalSupportAnnouncementBtnText,
            href: supportDetails.technicalSupportAnnouncementBtnHref
          }) }
          <div className={ this.namespace('row') }>
            <div className={ this.namespace('col-lg-4', 'col-md-6', 'content-wrap') }>
              <TrayTitle>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:CallUs' />
              </TrayTitle>

              { this.renderCityContacts(supportDetails) }

              { this.renderFederatedPartner() }

              <div className={ this.namespace('contact-link-info') }>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:GlobalDirectory:GlobalDirectory' />
              </div>
              <Link urlArgs={ props.urlArgs } { ...props.urls.contactUs }
                data-eid={ eids.globalDirectory }
                className={ this.namespace('contact-link') }>
                <FormattedMessage id={ golf.globalDirectory } />
              </Link>
            </div>
            { this.renderRequestACall() }

            <div className={ this.namespace('col-lg-4', 'col-md-6', 'content-wrap', 'divider') }>
              { this.renderChatSection(supportDetails) }
            </div>

            <div className={ this.namespace('col-md-4', 'help-mobile', 'hidden-md-up') }>
              <TrayTitle>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:HelpCenter' />
              </TrayTitle>
              <p>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:HelpCenter:Explore' />
              </p>
              <div>
                <Link className={ this.namespace('btn', 'btn-default') }
                  urlArgs={ props.urlArgs }
                  { ...props.urls.help } data-eid={ eids.help } >
                  <FormattedMessage id={ golf.help } />
                </Link>
              </div>
            </div>
          </div>
          { this.renderSupportAnnouncement(supportDetails.technicalSupportAnnouncement) }
        </div>
      </TrayMenu>
    );
  }

  showSupportContacts() {
    this.setState({
      showPhoneNumbers: true
    });
  }

  renderChatSection(supportDetails) {
    // Make chat data available in a global so chat team code can render it
    if (this.isBrowser) {
      const uxel = window && window.ux && window.ux.eldorado;
      if (uxel) {
        const messages = this.props.messages;

        uxel.chat_data = {
          target: 'gdchat-container',
          utilityChatEnabled: this.props.utilityChatEnabled,
          messages: {
            ChatNow: messages['Shared:UtilityBar:SupportPhone:ChatNow'],
            Chat: messages['Shared:UtilityBar:SupportPhone:Chat'],
            ChatOffline: messages['Shared:UtilityBar:SupportPhone:ChatOffline'],
            ChatHours: supportDetails.liveChatHours
          }
        };
      }
    }

    // IMPORTANT: `#gdchat-container` is used by the Chat team to render their chat UI into.
    // Do not remove it or change it without talking to @clim & @mfloyd
    return (
      <div id='gdchat-container' />
    );
  }

  renderRequestACall() {
    const props = this.props;

    if (!props.requestACallEnabled) {
      return null;
    }

    return (
      <div className={ this.namespace('col-lg-4', 'col-md-6', 'content-wrap', 'divider') }>
        <div className={ this.namespace('request-a-call') } >
          <TrayTitle>
            <FormattedMessage id='Sales:ContactTray:RequestACall:CTA' />
          </TrayTitle>

          <p>
            <FormattedMessage id='Sales:ContactTray:RequestACall:avoidTheWait' />
          </p>

          <Link
            className={ this.namespace('btn btn-default') }
            urlArgs={ props.urlArgs }
            data-eid='uxp.hyd.utility_bar.request_a_call.click'
            { ...props.urls.requestACall }
          >
            <FormattedMessage id='Sales:ContactTray:RequestACall:CTA' />
          </Link>
        </div>
      </div>
    );
  }

  /**
   * Render the support announcement section if needed
   *
   * @param {Object} announcement value from supportDetails.technicalSupport{AllChannels}
   * @param {boolean} [allChannels] flag denoting whether we're rendering the `AllChannels` support announcement
   * @param {Object} [buttonInfo] settings for the button to show next to the AllChannels announcement if applicable
   * @returns {React.ReactElement} The support announcement as a React element, or null if none needed
   */
  renderSupportAnnouncement(announcement, allChannels = false, buttonInfo = {}) {
    const className = allChannels ? 'support-announcement-all' : 'support-announcement';
    const announcementsTemplate =
    (<div className={ this.namespace(className) }>
      <span className={ this.namespace('support-announcement-text') }>
        <Icon name='information' />
        { announcement }
      </span>
      {
        buttonInfo.href ? <span className={ this.namespace('support-announcement-btn') }>
          <Link
            className={ this.namespace('btn', 'btn-sm', 'btn-default') }
            urlArgs={ this.props.urlArgs }
            data-eid='uxp.hyd.utility_bar.support_announcement_btn.click'
            href={ buttonInfo.href }>
            { buttonInfo.label }
          </Link>
        </span> : null
      }
    </div>);

    if (announcement) {
      if (!allChannels) {
        return (
          <div className={ this.namespace('row') }>
            <div className={ this.namespace('col-xs-12') }>
              { announcementsTemplate }
            </div>
          </div>
        );
      }
      return announcementsTemplate;

    }
    return null;
  }
}

ContactTray.propTypes = {
  ...NamespaceShape,
  supportContacts: PropTypes.object.isRequired,
  shopperId: PropTypes.string,
  contactTrayEnabled: PropTypes.bool,
  requestACallEnabled: PropTypes.bool,
  utilityChatEnabled: PropTypes.bool
};

ContactTray.defaultProps = {
  contactTrayEnabled: false,
  requestACallEnabled: false,
  utilityChatEnabled: false
};

export default withNamespace(ContactTray);
